import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { Button, InputLabel } from '@wajeez-web/components/ui';
import { ColorPickerFormType, TextFormType } from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { SketchPicker, TwitterPicker } from 'react-color';

export interface FormColorPickerInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: ColorPickerFormType;

  shape?: 'circle' | 'squre';
  color?: string;
  bg?: 'primary' | 'secondary' | string;
  disabled?: boolean;
  size?: 'sm' | 'med' | 'lg';
  prefix?: React.ReactElement;
  suffix?: JSX.Element | null;
  pickerType?: 'Sketch' | 'Twitter';
  colors?: string[];
}

const FormColorPickerInput = <TFormFields extends FieldValues>({
  fieldType,
  shape = 'squre',
  color = 'textPrimary',
  bg = 'white',
  prefix,
  suffix,
  size,
  disabled,
  colors,
  pickerType = 'Sketch',
  ...props
}: FormColorPickerInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,
      ...props.rules,
    },
  });

  const { dir } = useCommonSelector((state) => state.common.settings);

  const { t } = useTranslation();

  const [showColors, setShowColors] = useState(false);
  const handleChange = (color: any) => {
    field.onChange(color.hex);
    field.onBlur();
  };
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowColors(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [setShowColors]);

  const isError = fieldState.error && fieldState.isTouched;
  const getPicker = () => {
    switch (pickerType) {
      case 'Sketch':
        return (
          <SketchPicker
            color={field.value || ''}
            onChangeComplete={handleChange}
            className={`input focus:ring-opacity-0 mt-2 focus:ring-ring-color absolute z-50 ${
              size === 'med' && 'py-3'
            }  focus:ring-4 cursor-pointer   focus:ring-offset-0  ${
              shape === 'circle' ? 'rounded-full' : 'rounded-md'
            } bg-${bg} text-${color} focus:border-primary block w-full pe-14 sm:text-sm border-gray-300 `}
          />
        );
      case 'Twitter':
        return (
          <TwitterPicker
            color={field.value || ''}
            colors={colors}
            onChangeComplete={handleChange}
            triangle={'top-right'}
            width={'170px'}
            className={`input focus:ring-opacity-0 mt-4 focus:ring-ring-color absolute z-50 ${
              size === 'med' && 'py-1'
            }  focus:ring-4 cursor-pointer   focus:ring-offset-0  ${
              shape === 'circle' ? 'rounded-full' : 'rounded-md'
            } bg-${bg} text-${color} focus:border-primary block w-full  sm:text-sm border-gray-300 `}
          />
        );

      default:
        return null;
    }
  };
  return (
    <div className="w-[200px]">
      {fieldType.label && (
        <InputLabel
          text={
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          }
          name={field.name}
          required={fieldType.required}
        />
      )}
      <div
        className={`relative ${
          shape === 'circle' ? 'rounded-full' : 'rounded-md'
        } `}
      >
        <div className="flex space-s-2 align-middle w-auto border-none">
          <Button
            disabled={disabled}
            title="Pick Color"
            onClick={() => setShowColors(!showColors)}
          />{' '}
          {field.value && (
            <div
              style={{ backgroundColor: field.value }}
              className="w-8 h-8 rounded-full "
            ></div>
          )}
        </div>
        <div ref={ref}>{showColors && getPicker()}</div>
      </div>
    </div>
  );
};

export default FormColorPickerInput;
